import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Clock, Zap, Gift } from "lucide-react";
import { useSelector } from "react-redux";
import { UserModelData } from "../../types";
import useThemeHook from "../hooks/useThemeHook";
import piNetworkAdsManager from "../utils/PiNetworkAdsManager";

const EVENT_INTERVAL = 60 * 60 * 1000; // 1 hour
const BONUS_ATH = 1.0;

const HourlyBonusEvent: React.FC = () => {
  const theme = useThemeHook();
  const isDark = theme === "dark";
  const userData: UserModelData = useSelector((state: any) => state?.actualUser);
  const [timeLeft, setTimeLeft] = useState(0);
  const [isReady, setIsReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [claimed, setClaimed] = useState(false);

  const isPro = userData?.user?.isLifetimePro ||
    (userData?.user?.isPro && userData?.user?.proExpiry &&
      new Date(userData.user.proExpiry.seconds * 1000) > new Date());

  useEffect(() => {
    if (isPro) return;

    const calcTimeLeft = () => {
      const lastEvent = localStorage.getItem("lastHourlyBonus");
      const now = Date.now();

      if (!lastEvent) {
        // First time — set next event in 30 min to hook them fast
        localStorage.setItem("lastHourlyBonus", (now - EVENT_INTERVAL / 2).toString());
      }

      const nextEvent = Number(lastEvent || (now - EVENT_INTERVAL / 2)) + EVENT_INTERVAL;
      const remaining = Math.max(0, nextEvent - now);

      if (remaining <= 0) {
        setIsReady(true);
        setClaimed(false);
        setTimeLeft(0);
      } else {
        setIsReady(false);
        setTimeLeft(remaining);
      }
    };

    calcTimeLeft();
    const interval = setInterval(calcTimeLeft, 1000);
    return () => clearInterval(interval);
  }, [isPro]);

  const formatTime = (ms: number) => {
    const totalSec = Math.floor(ms / 1000);
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const handleClaim = useCallback(async () => {
    if (loading || !isReady) return;
    setLoading(true);
    try {
      await piNetworkAdsManager.handleRewardedAdFlowFront(userData?.user?.id, () => {
        localStorage.setItem("lastHourlyBonus", Date.now().toString());
        setClaimed(true);
        setIsReady(false);
        setTimeout(() => setClaimed(false), 3000);
      });
    } catch {
      // Ad failed
    } finally {
      setLoading(false);
    }
  }, [loading, isReady, userData?.user?.id]);

  if (isPro || claimed) return null;

  const progress = 1 - timeLeft / EVENT_INTERVAL;

  return (
    <div className={`rounded-2xl border overflow-hidden ${
      isDark ? "bg-slate-800/50 border-slate-700/50" : "bg-white border-gray-200 shadow-sm"
    }`}>
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className={`p-1.5 rounded-lg ${isReady ? "bg-emerald-100" : isDark ? "bg-slate-700" : "bg-indigo-50"}`}>
              {isReady ? (
                <Gift size={16} className="text-emerald-500" />
              ) : (
                <Clock size={16} className={isDark ? "text-indigo-400" : "text-indigo-500"} />
              )}
            </div>
            <span className={`font-black text-sm ${isDark ? "text-white" : "text-gray-900"}`}>
              {isReady ? "Bonus Ready!" : "Hourly Bonus"}
            </span>
          </div>

          {isReady ? (
            <div className="flex items-center gap-1 bg-emerald-100 px-2 py-1 rounded-full animate-pulse">
              <Zap size={12} className="text-emerald-500" fill="currentColor" />
              <span className="text-[10px] font-black text-emerald-700">+{BONUS_ATH} ATH</span>
            </div>
          ) : (
            <span className={`text-sm font-mono font-bold ${isDark ? "text-indigo-400" : "text-indigo-600"}`}>
              {formatTime(timeLeft)}
            </span>
          )}
        </div>

        {/* Progress bar */}
        <div className={`h-2 rounded-full overflow-hidden mb-3 ${isDark ? "bg-slate-700" : "bg-gray-100"}`}>
          <motion.div
            className={`h-full rounded-full ${isReady ? "bg-emerald-500" : "bg-gradient-to-r from-indigo-500 to-purple-500"}`}
            initial={{ width: 0 }}
            animate={{ width: `${progress * 100}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>

        {isReady ? (
          <button
            onClick={handleClaim}
            disabled={loading}
            className="w-full py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-black text-sm flex items-center justify-center gap-2 active:scale-95 transition-transform shadow-lg shadow-emerald-500/30 disabled:opacity-50"
          >
            {loading ? (
              <span className="animate-pulse">Loading Ad...</span>
            ) : (
              <>
                <Zap size={14} fill="white" />
                Watch Ad & Claim +{BONUS_ATH} ATH
              </>
            )}
          </button>
        ) : (
          <p className={`text-[10px] text-center font-medium ${isDark ? "text-slate-500" : "text-gray-400"}`}>
            Stay active! Bonus unlocks every hour ⏰
          </p>
        )}
      </div>
    </div>
  );
};

export default HourlyBonusEvent;
